<script setup>
import {computed} from 'vue'
import PagePath from '@/common/PagePath'
import Button from "./Button.vue"
import {useToastsStore} from '@/stores/toasts'
import {useModalsStore} from '@/stores/modals'
import {useItemDataStore} from '@/stores/itemData'
import {useFormStore} from '@/stores/form'

const props = defineProps({
    formId: String,
    field: Object,
    id: [String, Number, Object], // Added Object to type definition
    selectContentMode: Boolean,
    onChange: Function,
    removeDisabledBtn: Boolean
})

// Pinia Stores
const toastsStore = useToastsStore()
const modalsStore = useModalsStore()
const itemDataStore = useItemDataStore()
const formStore = computed(() => props.formId ? useFormStore(props.formId) : null)

// --- Computed ---

const currentData = computed(() => formStore.value?.currentData || {})

const tableName = computed(() => formStore.value?.tableName)

/**
 * Normalizes the 'id' prop. It handles legacy JSON strings,
 * objects with an 'internal' key, or raw IDs.
 */
const normalizedId = computed(() => {
    let raw = props.id
    if (!raw) return null

    if (typeof raw === 'string' && raw.includes('{')) {
        try {
            raw = JSON.parse(raw)
        } catch (e) { /* fallback to raw */
        }
    }

    if (typeof raw === 'object' && raw !== null) {
        return raw.internal
    }

    return raw
})

const pageId = computed(() => {
    return props.selectContentMode ? null : parseInt(normalizedId.value)
})

const contentId = computed(() => {
    if (props.selectContentMode && normalizedId.value) {
        const idStr = String(normalizedId.value)
        // Handle "c123" format for content IDs
        const id = idStr.startsWith('c') ? idStr.substring(1) : idStr
        return parseInt(id)
    }
    return null
})

// --- Methods ---

const internalLink = () => {
    modalsStore.show({
        componentName: 'SiteTreeModal',
        obj: {
            selectContentMode: props.selectContentMode,
            pageId: pageId.value,
            pageContentLinkId: contentId.value,
            onPageSelect: onPageSelect,
            onContentSelect: props.onChange,
        }
    })
}

/**
 * Validates the page selection, specifically checking for
 * hierarchy loops when editing the 'pages' table.
 */
const onPageSelect = async (selectedPageId) => {
    if (tableName.value === 'pages' && props.field?.name === 'parent') {

        // 1. Cannot be own parent
        if (selectedPageId === currentData.value.id) {
            toastsStore.add({
                body: 'A page cannot be the parent of itself.',
                colourScheme: 'warning'
            })
            return false
        }

        // 2. Cannot be child of descendant (Recursive check)
        const isDescendant = await checkIsDescendant(currentData.value.id, selectedPageId)

        if (isDescendant) {
            toastsStore.add({
                body: 'A page cannot be the child of its descendant.',
                colourScheme: 'warning'
            })
            return false
        }
    }

    props.onChange(selectedPageId)
}

/**
 * Recursive async function to check if the page being edited (formPageId)
 * exists anywhere in the ancestry chain of the newly selected parent (targetPageId).
 */
const checkIsDescendant = async (formPageId, targetPageId) => {
    if (!targetPageId) return false

    const parentPage = await itemDataStore.get({
        tableName: 'pages',
        id: targetPageId
    })

    if (parentPage.parent === formPageId) {
        return true
    } else if (parentPage.parent) {
        return await checkIsDescendant(formPageId, parentPage.parent)
    }

    return false
}
</script>

<template>
    <div class="d-flex align-items-center">
        <Button class="me-2 border" @click="internalLink">Browse…</Button>

        <p v-if="pageId || contentId" class="well m-0 p-1 text-nowrap">
            <PagePath :page-id="pageId" :page-content-id="contentId"/>
            <Button
                v-if="!removeDisabledBtn"
                class="btn-sm"
                @click="onChange(0)"
            >
                <i class="bi-x-lg"/>
            </Button>
        </p>
    </div>
</template>